import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  styled,
} from '@mui/material';
import { Control, useForm } from 'react-hook-form';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import DateInput from './form/DateInput';
import { DateTime } from 'luxon';

const PREFIX = 'AcivateDialog';

const classes = {
  form: `${PREFIX}-form`,
};

const StyledForm = styled(Box)(({ theme }) => ({
  [`&.${classes.form}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
}));

interface ActivateDialogProps {
  title: string;
  message: string;
  forActivate: boolean;
  handleOk: (start?: string, end?: string) => void;
  handleCancel: () => void;
  open: boolean;
  minEndDateISO?: string;
}

export default function ActivateDialog(props: ActivateDialogProps) {
  const { t } = useTranslation('common');

  const {
    title,
    message,
    forActivate,
    handleOk,
    handleCancel,
    open,
    minEndDateISO,
    ...other
  } = props;

  const formHook = useForm({
    mode: 'onTouched',
    defaultValues: { StartDate: null, EndDate: null },
  });

  const yesterday = () => {
    return DateTime.now().minus({ days: 1 }).toISODate();
  };

  const onSubmit = (data: any) => {
    let start: string | undefined;
    let end: string | undefined;
    if (forActivate) {
      start = data.StartDate
        ? data.StartDate.toFormat('yyyy-MM-dd')
        : yesterday();
      end = data.EndDate ? data.EndDate.toFormat('yyyy-MM-dd') : 'clear';
    } else {
      const minEnd = minEndDateISO
        ? DateTime.fromISO(minEndDateISO)
        : undefined;
      if (data.EndDate) {
        end = data.EndDate.toFormat('yyyy-MM-dd');
      } else {
        let fallback = yesterday();
        if (minEnd && DateTime.fromISO(fallback) < minEnd) {
          fallback = minEnd.toFormat('yyyy-MM-dd');
        }
        end = fallback;
      }
    }

    handleOk(start, end);
    formHook.reset();
  };

  const onCancel = () => {
    handleCancel();
    formHook.reset();
  };

  return (
    <Dialog disableEscapeKeyDown maxWidth="xs" open={open} {...other}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <StyledForm className={classes.form}>
            <Typography>{message}</Typography>
            {forActivate && (
              <DateInput
                label={t('common.effectiveStartDate')}
                name={'StartDate'}
                control={formHook.control as Control<any>}
                required={false}
              />
            )}
            <DateInput
              label={t('common.effectiveEndDate')}
              name={'EndDate'}
              control={formHook.control as Control<any>}
              required={false}
              minDate={
                !forActivate && minEndDateISO
                  ? DateTime.fromISO(minEndDateISO)
                  : undefined
              }
            />
          </StyledForm>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          color="primary"
          onClick={formHook.handleSubmit(onSubmit)}>
          {t('common.ok')}
        </Button>
        <Button variant="contained" color="secondary" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
